import * as React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';

import * as api from '../../../../api';

import styles from './index.module.css';
import defaultStyles from '../../../../styles/formSetStyles.module.css';

import SearchIcon from '@material-ui/icons/Search';

import * as AllergiesPage from '../';

import {
  AutocompleteAsyncInput,
  Suggestion,
} from '../../../../components/AutocompleteAsyncInput';
import { Button } from '../../../../components/Button';
import { InfoMessage } from '../../../../components/InfoMessage';
import { getOptionsIntersection } from '../../../../components/utils';
import { SelectInput } from '../../../../components/SelectInput';
import { TextInput } from '../../../../components/TextInput';

///////////////////////////////////////////////////
////// TYPES

export interface SelectedAllergy extends api.Allergy.StandardAllergy {
  id: string;
}

export interface AllergyToAdd {
  selectedAllergy: SelectedAllergy;
  selectedReaction: api.Allergy.Reaction;
  selectedSeverity: api.Allergy.Severity;
}

interface Props extends WithTranslation {
  selectedAllergy: SelectedAllergy | null;
  severityOptions: api.Allergy.Severity[];
  addAllergy: (allergyToAdd: AllergyToAdd) => void;
  handleSelect: (suggestion: Suggestion<SelectedAllergy>) => void;
  getAllergySuggestions: (
    input: string,
  ) => Promise<Suggestion<SelectedAllergy>[]>;
  initialText: string;
}

interface State {
  errorMessage?: string;
  selectedReaction: api.Allergy.Reaction | null;
  selectedSeverity: api.Allergy.Severity | null;
}

///////////////////////////////////////////////////
////// COMPONENT

class ModalBody extends React.Component<Props, State> {
  state: State = {
    selectedReaction: null,
    selectedSeverity: null,
  };

  handleContinue = () => {
    const { selectedAllergy, t } = this.props;
    const { selectedReaction, selectedSeverity } = this.state;

    if (!selectedAllergy || !selectedReaction || !selectedSeverity) {
      this.setState({
        errorMessage: t('Please provide more details regarding your allergy'),
      });
      return;
    }

    this.setState({ errorMessage: '' });
    this.props.addAllergy({
      selectedAllergy,
      selectedReaction,
      selectedSeverity,
    });
  };

  render() {
    const { selectedAllergy, handleSelect, getAllergySuggestions, t } =
      this.props;
    const { selectedReaction, selectedSeverity, errorMessage } = this.state;

    if (selectedAllergy) {
      const reactionOptions = Object.values(api.Allergy.Reaction).map(
        (reaction) => ({
          display: t(AllergiesPage.splitAndUpperFirst(reaction)),
          key: reaction,
          value: reaction,
        }),
      );

      const severityOptions = getOptionsIntersection(
        api.Allergy.Severity,
        this.props.severityOptions,
      ).map((severity) => ({
        display: t(AllergiesPage.splitAndUpperFirst(severity)),
        key: severity,
        value: severity,
      }));

      return (
        <div>
          {errorMessage ? (
            <div className={styles.InfoMessage}>
              <InfoMessage isError={true}>{errorMessage}</InfoMessage>
            </div>
          ) : null}
          <div>
            <label htmlFor="allergy-modal-name" className={styles.Label}>
              {t('Allergy')}
            </label>
            <TextInput
              id="allergy-modal-name"
              value={t(selectedAllergy?.displayName)}
              disabled={true}
            />
          </div>
          <div className={styles.MarginTop}>
            <label htmlFor="allergy-modal-reaction" className={styles.Label}>
              {t('Allergic Reaction')}
            </label>
            <SelectInput
              id="allergy-modal-reaction"
              value={selectedReaction || undefined}
              onChange={(reaction: api.Allergy.Reaction) => {
                this.setState({ selectedReaction: reaction });
              }}
              options={reactionOptions}
            />
          </div>
          <div className={styles.MarginTop}>
            <label htmlFor="allergy-modal-severity" className={styles.Label}>
              {t('Severity')}
            </label>
            <SelectInput
              id="allergy-modal-severity"
              value={selectedSeverity || undefined}
              onChange={(severity: api.Allergy.Severity) => {
                this.setState({ selectedSeverity: severity });
              }}
              options={severityOptions}
            />
          </div>
          <div className={defaultStyles.ButtonSubmitWrapper}>
            <Button
              type="button"
              children={t('Continue')}
              onClick={this.handleContinue}
            />
          </div>
        </div>
      );
    }

    return (
      <AutocompleteAsyncInput
        onSelect={(selected: Suggestion<SelectedAllergy>) =>
          handleSelect(selected)
        }
        getSuggestions={getAllergySuggestions}
        placeholder={t(AllergiesPage.SEARCH_PLACEHOLDER)}
        icon={<SearchIcon className={styles.SearchIcon} />}
        initialString={this.props.initialText}
        emptyLabel={t('No matches found.')}
        promptText={t('Type to search...')}
      />
    );
  }
}

export default withTranslation()(ModalBody);
